import React from 'react';
import { Users, MoreVertical, Edit, Trash2, Eye } from 'lucide-react';
import { createPageUrl } from '../../../utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

const statusColors = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-700',
  terminated: 'bg-red-100 text-red-700',
};

const employmentColors = {
  permanent: 'bg-blue-100 text-blue-700',
  contract: 'bg-amber-100 text-amber-700',
  probation: 'bg-purple-100 text-purple-700',
  intern: 'bg-cyan-100 text-cyan-700',
};

function LoadingSkeleton() {
  return Array(5)
    .fill(0)
    .map((_, i) => (
      <tr key={i} className="border-b">
        <td className="px-4 py-3">
          <div className="h-10 w-48 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3">
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3 hidden md:table-cell">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3 hidden lg:table-cell">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3">
          <div className="h-6 w-20 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3 hidden md:table-cell">
          <div className="h-6 w-20 bg-gray-200 rounded animate-pulse"></div>
        </td>
        <td className="px-4 py-3">
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
        </td>
      </tr>
    ));
}

function EmptyState() {
  return (
    <tr>
      <td colSpan={7} className="text-center py-12">
        <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-gray-500">Tidak ada data pegawai</p>
      </td>
    </tr>
  );
}

function EmployeeRow({ emp, getDepartmentName, getPositionName, onDelete }) {
  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-medium">
            {emp.full_name?.charAt(0) || 'U'}
          </div>
          <div>
            <p className="font-medium">{emp.full_name}</p>
            <p className="text-sm text-gray-500">{emp.email}</p>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 font-mono text-sm">{emp.employee_id}</td>
      <td className="px-4 py-3 hidden md:table-cell">{getDepartmentName(emp.department_id)}</td>
      <td className="px-4 py-3 hidden lg:table-cell">{getPositionName(emp.position_id)}</td>
      <td className="px-4 py-3">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[emp.status] || statusColors.active}`}
        >
          {emp.status === 'active'
            ? 'Aktif'
            : emp.status === 'inactive'
              ? 'Tidak Aktif'
              : 'Berhenti'}
        </span>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full border ${employmentColors[emp.employment_status] || ''}`}
        >
          {emp.employment_status?.charAt(0).toUpperCase() + emp.employment_status?.slice(1) || '-'}
        </span>
      </td>
      <td className="px-4 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 hover:bg-gray-100 rounded-lg">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => (window.location.href = createPageUrl(`EmployeeDetail?id=${emp.id}`))}
            >
              <Eye className="w-4 h-4 mr-2" /> Lihat Detail
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => (window.location.href = createPageUrl(`EmployeeForm?id=${emp.id}`))}
            >
              <Edit className="w-4 h-4 mr-2" /> Edit
            </DropdownMenuItem>
            <DropdownMenuItem className="text-red-600" onClick={() => onDelete(emp.id)}>
              <Trash2 className="w-4 h-4 mr-2" /> Hapus
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}

export default function EmployeeTable({
  employees,
  loading,
  getDepartmentName,
  getPositionName,
  onDelete,
}) {
  return (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Pegawai</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">NIP</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden md:table-cell">
                Departemen
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden lg:table-cell">
                Jabatan
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Status</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 hidden md:table-cell">
                Tipe
              </th>
              <th className="w-12 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingSkeleton />
            ) : employees.length === 0 ? (
              <EmptyState />
            ) : (
              employees.map(emp => (
                <EmployeeRow
                  key={emp.id}
                  emp={emp}
                  getDepartmentName={getDepartmentName}
                  getPositionName={getPositionName}
                  onDelete={onDelete}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
